import React, { useCallback, useState } from 'react';

import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import styled from 'styled-components';

import { Collapsible } from '@techstyle/react-components';
import { FormattedMessage } from '@techstyle/react-intl';
import { useDomain } from '@techstyle/redux-core';

import { desktop, mobile } from '../../styles';
import { BasicButton } from '../../styles/button';
import { fadeUp } from '../../utils/animations';
import {
  isNotDualSize,
  getNonBraSizes,
  getInfoSizeList,
} from '../../utils/sizes';
import { Component as SizeFilter } from '../SizeFilter';
import SizeFilterModalIcons from '../SizeFilterModalIcons/SizeFilterModalIcons';

const StyledScreenWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 50px 98px 20px;
  ${mobile`
    padding: 50px ${({ theme }) => theme.spacing.small}px 20px;
  `}
`;

const Title = styled.div`
  font-size: 16px;
  margin-bottom: ${({ theme }) => theme.spacing.tiny}px;
  text-align: center;
  font-weight: 700;
  letter-spacing: 0.3px;
  line-height: 19px;
  animation: ${fadeUp} 400ms ease-in 500ms;
  animation-fill-mode: both;

  ${mobile`
    margin-bottom: 12px;
    text-align: left;
  `}
`;

const BodyCopy = styled.div`
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  text-align: center;
  margin: 0px;
  align-self: center;
  animation: ${fadeUp} 400ms ease-in 500ms;
  animation-fill-mode: both;

  ${mobile`
    text-align: left;
    align-self: flex-start;
  `}
`;

const CategoriesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-top: 20px;
  margin-top: 20px;
  border-top: solid 1px ${({ theme }) => theme.colors.lavender400};

  ${desktop`
    margin-bottom: ${({ isCategoryOpen }) => (isCategoryOpen ? 20 : 0)}px;
  `}

  ${mobile`
    margin-top:${({ theme }) => theme.spacing.small}px;
    padding-top:${({ theme }) => theme.spacing.small}px;
  `}
`;
const CategoryCollapsible = styled(Collapsible)``;

const CategoryCollapsibleWrapper = styled.div`
  transition-property: transform;
  transition-duration: 400ms;

  &.category-enter.category-enter-active {
    transition: opacity 400ms ease-in, margin 400ms ease-out;
  }

  &.category-exit.category-exit-active {
    transition: opacity 400ms ease-out, margin 400ms ease-in;
  }

  &.category-enter,
  &.category-exit.category-exit-active,
  &.category-exit-done {
    opacity: 0;
  }

  &.category-enter.category-enter-active,
  &.category-exit {
    opacity: 1;
  }
`;

const CategoryCollapsibleTrigger = styled(Collapsible.Button)``;

const CollapsibleCategoryContent = styled(Collapsible.Content)`
  background-color: ${({ theme }) => theme.colors.lavender200};
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const Category = styled.div`
  background-color: ${({ theme }) => theme.colors.lavender200};
  border-radius: 8px;
  margin-bottom: ${({ isOpen, theme }) => (isOpen ? 0 : theme.spacing.tiny)}px;
  padding: 14px;
  border-bottom-left-radius: ${({ isOpen }) => (isOpen ? 0 : 8)}px;
  border-bottom-right-radius: ${({ isOpen }) => (isOpen ? 0 : 8)}px;
`;

const CategoryWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
`;

const StyledIconWrapper = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.white};
`;

const SelectedSizeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
  line-height: 14px;
  text-align: center;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.subdued};
  margin-left: ${({ theme }) => theme.spacing.small}px;
`;

const Size = styled.div`
  font-size: 16px;
  line-height: 19px;
  letter-spacing: 0.3px;
  color: ${({ theme }) => theme.colors.default};
`;

const EditButton = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  justify-content: flex-end;
  align-self: center;
  font-weight: 700;
  font-size: 12px;
  line-height: 14px;
  text-align: center;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.active};
  :hover {
    cursor: pointer;
  }
`;

const StyledSizeFilter = styled(SizeFilter)`
  display: flex;
  flex: 1;
  padding: 0px 14px 18px;

  & ${SizeFilter.FilterLabel} {
    display: none;
  }

  & ${SizeFilter.SizeSectionWrap} {
    z-index: 1;
  }

  & ${SizeFilter.SizeCategoryWrapper} {
    margin-bottom: 0;

    ${SizeFilter.BraCategoryLabel} {
      color: ${({ theme }) => theme.colors.subdued};
      font-weight: 400;
      font-size: 12px;
      line-height: 14px;
      letter-spacing: 1.75px;
      margin-bottom: ${({ theme }) => theme.spacing.tiny}px;
      align-self: start;
    }

    ${SizeFilter.BraCategoryLabel}:nth-of-type(4) {
      margin-top: ${({ theme }) => theme.spacing.noderate}px;
    }
  }

  & ${SizeFilter.SizeCheckbox} {
    margin: 4px;

    > ${SizeFilter.SizeCheckbox.InputWrapper} {
      ${SizeFilter.SizeCheckbox.Label} {
        top: 14px;
        font-weight: normal;
      }

      ${SizeFilter.SizeCheckbox.CustomCheckbox} {
        background-color: ${({ theme }) => theme.colors.white};
        border: 1px solid ${({ theme }) => theme.colors.lavender300};
        width: 50px;
        height: 50px;
      }

      input:checked + ${SizeFilter.SizeCheckbox.CustomCheckbox} {
        background-color: ${({ theme }) => theme.colors.default};
        border: none;
        padding: 0;
      }

      input:checked ~ ${SizeFilter.SizeCheckbox.Label} {
        color: ${({ theme }) => theme.colors.white};
        line-height: 17px;
        top: 14px;
      }

      input:active
        + ${SizeFilter.SizeCheckbox.CustomCheckbox},
        input:focus
        + ${SizeFilter.SizeCheckbox.CustomCheckbox} {
        border: 1px solid ${({ theme }) => theme.colors.gray250};
      }
    }
  }
`;

export const NextButton = styled(BasicButton).attrs({
  'aria-label': 'continue button',
  tabIndex: 0,
})`
  width: 343px;
  background: ${({ theme }) => theme.colors.active};
  text-transform: uppercase;
  border: 0px;
  margin-top: ${({ theme }) => theme.spacing.small}px;

  ${mobile`
    width: 307px;
  `}
`;

const EditButtonComponent = ({ isCategoryOpen }) => {
  return (
    <EditButton>
      {isCategoryOpen ? (
        <FormattedMessage
          id="site_product_grid.save_size"
          defaultMessage="Save"
        />
      ) : (
        <FormattedMessage
          id="site_product_grid.edit_size"
          defaultMessage="Edit"
        />
      )}
    </EditButton>
  );
};

EditButtonComponent.propTypes = {
  isCategoryOpen: PropTypes.bool,
};

const duration = 400;
const onEnter = node => {
  node.style.marginTop = `-${node.offsetHeight}px`;
  node.style.marginBottom = `0px`;
};
const onEntering = node => {
  node.style.marginTop = '';
  node.style.marginBottom = '';
};
const onExit = node => {
  node.style.marginTop = '';
  node.style.marginBottom = '';
};
const onExiting = node => {
  node.style.marginTop = `-${node.offsetHeight}px`;
  node.style.marginBottom = `0px`;
};

function SizeFilterModalCategoryScreen({
  className,
  advanceScreen,
  onSetNewSelectedSizes,
  selectedSizes,
  updatedSizeFilters = null,
}) {
  const { tld } = useDomain();
  const [error, setError] = useState(null);
  const [openedCategory, setOpenedCategory] = useState(null);
  const [categoriesCollapsibleStatus, setCategoriesCollapsibleStatus] =
    useState({
      size_bra: false,
      size_bralette: false,
      size_undie: false,
      size_lingerie: false,
    });

  const toggleCollapsible = useCallback((categoryName, isOpen) => {
    setCategoriesCollapsibleStatus(prevState => ({
      ...prevState,
      [categoryName]: !prevState[categoryName],
    }));
  }, []);

  const formatted = sizes =>
    sizes
      .map(size => ({
        id: size,
        label: size,
      }))
      .filter(_ => _);

  const braSizes = getInfoSizeList(tld, false, false, false);
  const braletteSizes = formatted(getNonBraSizes({ tld }));
  const undieSizes = braletteSizes;
  const lingerieSizes = braletteSizes;

  const checkBraSize = selectedSizes_ => {
    const [band, cup] =
      selectedSizes_.braSizes[0].match(/[a-z]+|[^a-z]+/gi) || [];
    return { band, cup };
  };

  const onOpen = useCallback(
    categoryName => {
      toggleCollapsible(categoryName);
      setOpenedCategory(categoryName);
    },
    [toggleCollapsible]
  );

  const onClose = useCallback(
    categoryName => {
      if (categoryName === 'size_bra') {
        // there is a risk a user picked a band size and not a cup size. Can't happen with other categories tho
        const braSize = checkBraSize(selectedSizes);
        if (braSize.band && !braSize.cup) {
          setError('SIZE_REQUIRED');
          return;
        }
      }
      toggleCollapsible(categoryName);
      setOpenedCategory(null);
    },
    [selectedSizes, toggleCollapsible]
  );

  const handleChooseBandSize = newCupSize => {
    setError(null);
    onSetNewSelectedSizes({
      braSizes: [newCupSize],
    });
  };

  const handleChooseSize = useCallback((sizeId, sizeField, newProductSize) => {
    setError(null);
    onSetNewSelectedSizes &&
      onSetNewSelectedSizes({ [sizeField]: [newProductSize] });
  }, []);

  const renderCategory = categoryName => {
    let categoryComponent = null;
    const sizes = updatedSizeFilters[categoryName] || [];
    // only categories with one size selected only will show and if the size is not dual or unique
    if (sizes?.length === 1) {
      switch (categoryName) {
        case 'size_bra':
          categoryComponent = isNotDualSize(selectedSizes.braSizes[0]) ? (
            <CategoryWrapper>
              <StyledIconWrapper>
                <SizeFilterModalIcons name="bra" />
              </StyledIconWrapper>
              <SelectedSizeWrapper>
                <FormattedMessage
                  defaultMessage="Bra"
                  id="site_product_grid.bra_label"
                />
                <Size>{selectedSizes.braSizes[0]}</Size>
              </SelectedSizeWrapper>
              <EditButtonComponent
                isCategoryOpen={categoriesCollapsibleStatus.size_bra}
              />
            </CategoryWrapper>
          ) : null;
          break;
        case 'size_bralette':
          categoryComponent = isNotDualSize(selectedSizes.braletteSizes[0]) ? (
            <CategoryWrapper>
              <StyledIconWrapper>
                <SizeFilterModalIcons name="bralette" />
              </StyledIconWrapper>
              <SelectedSizeWrapper>
                <FormattedMessage
                  defaultMessage="Bralette"
                  id="site_product_grid.bralette_label"
                />
                <Size>{selectedSizes.braletteSizes[0]}</Size>
              </SelectedSizeWrapper>
              <EditButtonComponent
                isCategoryOpen={categoriesCollapsibleStatus.size_bralette}
              />
            </CategoryWrapper>
          ) : null;
          break;
        case 'size_lingerie':
          categoryComponent = isNotDualSize(selectedSizes.lingerieSizes[0]) ? (
            <CategoryWrapper>
              <StyledIconWrapper>
                <SizeFilterModalIcons name="lingerie_sleep" />
              </StyledIconWrapper>
              <SelectedSizeWrapper>
                <FormattedMessage
                  defaultMessage="Lingerie / Sleep"
                  id="site_product_grid.lingerie_sleep_label"
                />
                <Size>{selectedSizes.lingerieSizes[0]}</Size>
              </SelectedSizeWrapper>
              <EditButtonComponent
                isCategoryOpen={categoriesCollapsibleStatus.size_lingerie}
              />
            </CategoryWrapper>
          ) : null;
          break;
        case 'size_undie':
          categoryComponent = isNotDualSize(selectedSizes.undieSizes[0]) ? (
            <CategoryWrapper>
              <StyledIconWrapper>
                <SizeFilterModalIcons name="undie" />
              </StyledIconWrapper>
              <SelectedSizeWrapper>
                <FormattedMessage
                  defaultMessage="Undie"
                  id="site_product_grid.undie_label"
                />
                <Size>{selectedSizes.undieSizes[0]}</Size>
              </SelectedSizeWrapper>
              <EditButtonComponent
                isCategoryOpen={categoriesCollapsibleStatus.size_undie}
              />
            </CategoryWrapper>
          ) : null;
          break;
        default:
          categoryComponent = null;
      }
    }
    const shouldShowCategory =
      !openedCategory || (!!openedCategory && openedCategory === categoryName);

    return (
      <CSSTransition
        classNames="category"
        key={categoryName}
        timeout={duration}
        onEnter={onEnter}
        onEntering={onEntering}
        onExit={onExit}
        onExiting={onExiting}
        in={categoryComponent && shouldShowCategory}
        mountOnEnter
      >
        <CategoryCollapsibleWrapper>
          <CategoryCollapsible
            key={categoryName}
            defaultOpen={false}
            isOpen={categoriesCollapsibleStatus[categoryName]}
            onOpen={() => onOpen(categoryName)}
            onClose={() => onClose(categoryName)}
          >
            <CategoryCollapsibleTrigger>
              <Category isOpen={categoriesCollapsibleStatus[categoryName]}>
                {categoryComponent}
              </Category>
            </CategoryCollapsibleTrigger>
            <CollapsibleCategoryContent transitionDuration={1000}>
              <StyledSizeFilter
                collapsible={false}
                braSizes={openedCategory === 'size_bra' ? braSizes : null}
                braletteSizes={
                  openedCategory === 'size_bralette' ? braletteSizes : null
                }
                lingerieSizes={
                  openedCategory === 'size_lingerie' ? lingerieSizes : null
                }
                menSizes={null}
                undieSizes={openedCategory === 'size_undie' ? undieSizes : null}
                sleepSizes={
                  openedCategory === 'size_lingerie' ? lingerieSizes : null
                }
                error={error}
                selectedSizes={selectedSizes}
                onChooseSize={handleChooseSize}
                onChooseBandSize={handleChooseBandSize}
              />
            </CollapsibleCategoryContent>
          </CategoryCollapsible>
        </CategoryCollapsibleWrapper>
      </CSSTransition>
    );
  };

  if (!updatedSizeFilters) {
    return null;
  }

  return (
    <StyledScreenWrapper>
      <Title>
        <FormattedMessage
          defaultMessage="Hey! We noticed you filtered by these sizes. Create a profile to save them and to shop what’s in stock."
          id="site_product_grid.modal_category_title"
        />
      </Title>
      <BodyCopy>
        <FormattedMessage
          defaultMessage="Is this correct?"
          id="site_product_grid.subtitle"
        />
      </BodyCopy>
      <CategoriesWrapper
        isCategoryOpen={Object.values(categoriesCollapsibleStatus).some(
          cat => !!cat
        )}
      >
        {Object.keys(updatedSizeFilters).map(categoryName =>
          renderCategory(categoryName)
        )}
      </CategoriesWrapper>
      {!openedCategory ? (
        <NextButton onClick={advanceScreen}>
          <FormattedMessage
            defaultMessage="NEXT"
            id="site_product_grid.next_button_label"
          />
        </NextButton>
      ) : null}
    </StyledScreenWrapper>
  );
}

SizeFilterModalCategoryScreen.propTypes = {
  advanceScreen: PropTypes.func.isRequired,
  onSetNewSelectedSizes: PropTypes.func,
  selectedSizes: PropTypes.object,
  updatedSizeFilters: PropTypes.object,
};

export default SizeFilterModalCategoryScreen;
